import * as React from "react";

import { ChevronLeft, ChevronRight } from "lucide-react";

import { LazyImg } from "@/components/helpers/ui/LazyImg";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { StatusBadge } from "@/components/ui/integration-card";
import { IntegrationInstanceCard } from "@/components/ui/integration-instance-card";

interface CarouselImage {
  src: string;
  alt: string;
}

interface IntegrationInstance {
  id: string;
  name: string;
  enabledBy: {
    name: string;
    avatar?: string;
  };
  enabledOn: string;
}

interface IntegrationFlyoutProps {
  children: React.ReactNode;
  integrationName: string;
  icon: React.ReactNode;
  status: "connected" | "disconnected" | "error";
  statusLabel: string;
  description: string;
  carouselImages: CarouselImage[];
  instances?: IntegrationInstance[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onConnect?: () => void;
  onDisconnectInstance?: (instanceId: string) => void;
  loading?: boolean;
}

function IntegrationFlyout({
  children,
  integrationName,
  icon,
  status,
  statusLabel,
  description,
  carouselImages,
  instances = [],
  open,
  onOpenChange,
  onConnect,
  onDisconnectInstance,
  loading = false,
}: IntegrationFlyoutProps) {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const totalSlides = carouselImages.length;

  const handlePrevious = () => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentSlide((prev) => Math.min(totalSlides - 1, prev + 1));
  };

  return (
    <Drawer direction="right" open={open} onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent className="max-w-3xl">
        <DrawerCloseButton />

        {/* Header */}
        <div className="border-b border-gray-300 bg-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-4">
                {icon}
                <div className="flex flex-col gap-2">
                  <h2 className="text-lg font-semibold text-gray-800 m-0">
                    {integrationName}
                  </h2>
                </div>
              </div>
              <StatusBadge status={status}>{statusLabel}</StatusBadge>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 space-y-6 overflow-y-auto overflow-x-visible">
          {/* Connect New Account Section */}
          <div className="border border-gray-300 rounded-md bg-white p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800 m-0">
                {instances.length > 0
                  ? `Connect another ${integrationName} account`
                  : `Connect a ${integrationName} account`}
              </h3>
              <Button
                intent="primary"
                variant="solid"
                size="md"
                onClick={onConnect}
                disabled={loading}
                className="px-4"
              >
                {loading ? "Connecting..." : "Connect"}
              </Button>
            </div>

            {/* Natural Carousel Strip */}
            <div className="relative w-full h-[257px] overflow-hidden rounded-md">
              <div
                className="flex h-full transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(-${currentSlide * 460}px)`, // Slide by 460px instead of 520px to show more hints
                  width: `${carouselImages.length * 520}px`,
                }}
              >
                {carouselImages.map((image, index) => (
                  <div key={index} className="flex items-center flex-shrink-0">
                    <div className="w-[500px] h-[257px] rounded-md overflow-hidden border border-gray-200">
                      <LazyImg
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {index < carouselImages.length - 1 && (
                      <div className="w-5" /> // 20px gap
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Overview Section */}
            <div className="space-y-3 mt-6">
              <div className="flex items-center justify-between">
                <h4 className="text-base font-semibold text-gray-800 m-0 flex-1">
                  Overview
                </h4>
                <div className="flex items-center gap-3">
                  <Button
                    intent="secondary"
                    variant="outline"
                    size="sm"
                    onClick={handlePrevious}
                    disabled={currentSlide === 0}
                    className={`px-1 py-1 ${currentSlide === 0 ? "opacity-40" : ""}`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    intent="secondary"
                    variant="outline"
                    size="sm"
                    onClick={handleNext}
                    disabled={currentSlide === totalSlides - 1}
                    className={`px-1 py-1 ${currentSlide === totalSlides - 1 ? "opacity-40" : ""}`}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <p className="text-base font-normal leading-relaxed text-gray-800 m-0">
                {description}
              </p>
            </div>
          </div>

          {/* Existing Instances Section */}
          {instances.length > 0 && (
            <div className="space-y-4">
              {instances.map((instance) => (
                <IntegrationInstanceCard
                  key={instance.id}
                  name={instance.name}
                  integrationName={integrationName}
                  enabledBy={instance.enabledBy}
                  enabledOn={instance.enabledOn}
                  onDisconnect={
                    onDisconnectInstance
                      ? () => onDisconnectInstance(instance.id)
                      : undefined
                  }
                  loading={loading}
                />
              ))}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export { IntegrationFlyout };
export type { CarouselImage, IntegrationFlyoutProps, IntegrationInstance };
